/**
 * A mixin for components that need to track when to reload semi-expensive resources,
 * like a procedural babylon mesh.
 */
export interface Reloadable {
  state: "stale" | "loaded";
}

/**
 * A mixin for components that need to track when to reload expensive resources asynchronously,
 * like a cache of position data from a server.
 *
 * `"failed"` means the fetch threw. Nothing retries it: only a changed spec rebuilds the
 * component, stale, and fetches again. A model is the exception: a failed model load records
 * its error on the mesh and ends `"loaded"` (see `reloadStaleMeshes`).
 */
export interface AsyncReloadable {
  state: "stale" | "loading" | "loaded" | "failed";
  abortController: AbortController;
}

/**
 * Records that a fetch threw, so its component stops reading as loading.
 *
 * Takes the load the fetch was started for, never one read back off the entity: a spec change
 * replaces the whole component, so by the time a fetch fails the entity may hold a newer load
 * that this failure says nothing about. An aborted fetch's load is left alone for the same
 * reason; whatever aborted it owns the component now.
 */
export function markLoadFailed(load: AsyncReloadable, signal: AbortSignal) {
  if (!signal.aborted) {
    load.state = "failed";
  }
}
